/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { ToolbarComponentCommonProps } from '..';
import { useAppDispatch, useAppSelector } from '@reducers/index';
import { AnalyticsEvents } from '@services/analytics/AnalyticsEvents';
import { getAnalytics } from '@services/analytics/AnalyticsService';
import { setOverviewMode } from '@g17eco/slices/surveySettings';
import { FormatOptionLabelMeta, SingleValue } from 'react-select';
import { PACK, QUESTION, SURVEY } from '@constants/terminology';
import { Option, SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { SurveyOverviewMode } from '@g17eco/types/surveyOverview';

export interface ScopeView {
  value: number;
  label: string;
  description: string;
}

const scopeViews: ScopeView[] = [
  {
    value: SurveyOverviewMode.ScopeGroups,
    label: `${SURVEY.CAPITALIZED_SINGULAR} ${PACK.PLURAL}`,
    description:
      `This the default view. Groups of ${QUESTION.PLURAL} follow the scope selected. ${QUESTION.CAPITALIZED_PLURAL} may appear in multiple groups.`,
  },
  {
    value: SurveyOverviewMode.QuestionList,
    label: 'Distinct list',
    description: `A list of unique ${QUESTION.PLURAL}`,
  },
  {
    value: SurveyOverviewMode.SdgAndScope,
    label: `SDG aligned ${PACK.PLURAL}`,
    description:
      `A combined view that picks out key Sustainable Development Goal aligned ${QUESTION.PLURAL} into groupings, with the remainder of the ${QUESTION.PLURAL} following the scope selected. ${QUESTION.CAPITALIZED_PLURAL} assigned in the SDG groups will not appear in the respective scope groups.`,
  },
];

const scopeViewOptions = scopeViews.map(({ value, label, description }) => ({
  label: (
    <div>
      <div className='strong pt-2'>View: {label}</div>
      <div className='pt-1 pb-2'>{description}</div>
    </div>
  ),
  value,
}));

const formatOptionLabel = (
  option: Option<number> | null,
  meta: FormatOptionLabelMeta<Option<number> | null>
) => {
  if (!option) {
    return <></>;
  }
  const selectedView = scopeViews.find((view) => view.value === option.value);
  if (meta.context === 'value' && selectedView) {
    return <span>View: {selectedView.label}</span>;
  }
  return option.label;
};

export const ScopeViewDropdown = (props: ToolbarComponentCommonProps) => {
  const { initiativeId, surveyId, defaultOverviewMode, handleSelectOverviewMode } = props;

  const dispatch = useAppDispatch();
  const surveyOverviewMode = useAppSelector((state) => state.surveySettings.overviewMode);

  const selectView = async (view: ScopeView) => {
    dispatch(setOverviewMode(view.value));
    const analytics = getAnalytics();
    if (surveyId) {
      analytics.track(AnalyticsEvents.SurveyGroupView, {
        initiativeId,
        surveyId,
        mode: view.value,
        modeLabel: view.label,
      });
    }
  };

  const selectedSort = scopeViewOptions.find((s) => s.value === (defaultOverviewMode ?? surveyOverviewMode)) ?? scopeViews[0];

  const handleSelect = (op:  SingleValue<Option<number> | null>) => {
    if (!op) {
      return;
    }
    const selectedScopeView = op as ScopeView;
    if (handleSelectOverviewMode) {
      handleSelectOverviewMode(selectedScopeView);
    } else {
      selectView(selectedScopeView);
    }
  };

  return (
    <div className='flex-grow-1 flex-lg-grow-0'>
      <SimpleTooltip text={'Click to choose view mode'}>
        <SelectFactory
          selectType={SelectTypes.SingleSelect}
          isSearchable={false}
          options={scopeViewOptions}
          defaultValue={selectedSort}
          formatOptionLabel={formatOptionLabel}
          onChange={(op) => handleSelect(op)}
        />
      </SimpleTooltip>
    </div>
  );
};
